import { useEffect, useState } from 'react';
import { API } from '../service/ProductsService';
import { useAuth } from '../utils/AuthContext';
import { useBasketMeta } from '../utils/basketStore';

// מחיקת חשבון (עמוד הפרופיל → spDeleteAccount). מסביר מה נמחק ומה נשאר (כמו במדיניות הפרטיות, סעיף 8),
// דורש סיסמה ואישור שזה סופי. אחרי ההצלחה - accountDeleted() ב-ZolBakfarApp: ניתוק והודעה.

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 12px', zIndex: 120, overflowY: 'auto' },
  dialog:  { width: '100%', maxWidth: 480, background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 16px 48px rgba(0,0,0,.25)' },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  title:   { margin: 0, fontSize: 20, fontWeight: 700, color: '#a8071a' },
  close:   { background: 'none', border: 'none', fontSize: 26, lineHeight: 1, color: '#59636e' },
  text:    { margin: '10px 0', lineHeight: 1.6 },
  list:    { margin: '0 0 12px', paddingInlineStart: 20, lineHeight: 1.7 },
  note:    { margin: '0 0 14px', padding: '8px 12px', background: '#eef4ff', color: '#1a3f9c', borderRadius: 10, fontSize: 14 },
  label:   { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 },
  input:   { width: '100%', padding: '10px 12px', fontSize: 16, border: '1px solid #d0d7de', borderRadius: 10, direction: 'ltr', textAlign: 'right' },
  check:   { display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0', cursor: 'pointer' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  danger:  { padding: '11px 18px', fontSize: 15, fontWeight: 700, border: 'none', borderRadius: 10, background: '#cf222e', color: '#fff' },
  cancel:  { padding: '10px 16px', fontSize: 15, fontWeight: 600, border: '1px solid #d0d7de', borderRadius: 10, background: '#fff', color: '#1f2328' },
  error:   { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '9px 12px', borderRadius: 10, marginBottom: 12, fontSize: 14 },
};

const DeleteAccountDialog = ({ onClose }) => {
  const { accountDeleted } = useAuth();
  const { list } = useBasketMeta();
  const shared = (list?.memberCount || 0) > 1;
  const [password, setPassword] = useState('');
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose, busy]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) { setError('נא להזין את הסיסמה'); return; }
    if (!sure) { setError('יש לסמן שהבנת שהמחיקה סופית'); return; }
    setBusy(true);
    setError(null);
    try {
      const resp = await API.deleteAccount(password);
      const status = resp?.[0]?.[0];
      if (status?.code === 0) { accountDeleted(); return; }
      if (status?.code !== 90) setError(status?.msg || 'המחיקה נכשלה, נסו שוב');
    } catch (err) {
      setError('אין חיבור לשרת, נסו שוב');
    }
    setBusy(false);
  };

  return (
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div style={s.dialog} role="dialog" aria-modal="true" aria-labelledby="delete-title">
        <div style={s.header}>
          <h2 id="delete-title" style={s.title}>מחיקת החשבון</h2>
          <button type="button" style={s.close} onClick={onClose} disabled={busy} aria-label="סגירה">×</button>
        </div>

        <p style={s.text}>המחיקה <strong>סופית ואי אפשר לבטל אותה</strong>. יימחקו:</p>
        <ul style={s.list}>
          <li>השם, הטלפון והסיסמה</li>
          <li>המוצרים המועדפים</li>
          <li>{shared ? 'החברות ברשימה המשותפת (הרשימה נשארת לשאר בני הבית)' : 'רשימת הקניות והרשימות הקודמות'}</li>
          <li>היסטוריית הפעילות בחשבון</li>
        </ul>
        <p style={s.text}>
          חיפושים, עדכוני מחיר ותוכן פידבק שנשלח יישארו באתר בלי שום קשר אליך.
          אפשר להירשם שוב בעתיד עם אותו מספר טלפון.
        </p>
        {!shared && (
          <p style={s.note}>רוצה לשמור את רשימת הקניות? אפשר לשלוח אותה לעצמך בוואטסאפ מטאב "רשימת קניות" לפני המחיקה.</p>
        )}

        <form onSubmit={submit} noValidate>
          {error && <div style={s.error} role="alert">{error}</div>}
          <label style={s.label} htmlFor="delete-password">הסיסמה שלך, לאישור</label>
          <input id="delete-password" style={s.input} type="password" value={password}
                 onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus />
          <label style={s.check}>
            <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} style={{ width: 18, height: 18 }} />
            הבנתי שהמחיקה סופית
          </label>
          <div style={s.actions}>
            <button type="submit" style={s.danger} disabled={busy}>{busy ? 'מוחק…' : 'מחיקת החשבון לצמיתות'}</button>
            <button type="button" style={s.cancel} onClick={onClose} disabled={busy}>ביטול</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteAccountDialog;
